import React, { useEffect } from 'react';
import { useMessageStore } from 'store/messageStore'; // Adjust the import path based on your store/messageStore';
import { useWebSocket } from 'hooks/useWebSocket';
import MessageHistory from 'components/MessageHistory/MessageHistory';
import SendForm from 'components/SendForm';

const Dashboard: React.FC = () => {
  const { 
    fetchMessages, 
    error, 
    clearError, 
    refreshMessages,
    totalCount,
    loading 
  } = useMessageStore();

  // Initialize WebSocket connection for real-time updates
  const { isConnected } = useWebSocket({
    onConnect: () => {
      console.log('Real-time connection established');
    },
    onDisconnect: () => {
      console.log('Real-time connection lost');
    },
    onError: (error) => {
      console.error('WebSocket error:', error);
    },
  });

  // Load initial messages on component mount
  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // Handle refresh
  const handleRefresh = () => {
    refreshMessages();
  };

  // Handle global errors
  const handleDismissError = () => {
    clearError();
  };

  if (error) {
    return (
      <div className="error-container">
        <div className="error-content">
          <h2>Unable to Load Dashboard</h2>
          <p className="error-message">{error}</p>
          <div className="error-actions">
            <button 
              onClick={() => fetchMessages()} 
              className="retry-button"
              type="button"
            >
              Retry
            </button>
            <button 
              onClick={handleDismissError} 
              className="dismiss-button"
              type="button"
            >
              Dismiss
            </button>
          </div>
        </div>
        
        <div className="error-info">
          <h3>Troubleshooting Tips</h3>
          <ul>
            <li>Ensure your Django server is running on the correct port</li>
            <li>Check that your API endpoints are accessible</li>
            <li>Verify your environment variables are set correctly</li>
            <li>Check the browser console for additional error details</li>
          </ul>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="dashboard-header__content">
          <div className="dashboard-header__left">
            <div className="dashboard-header__title">
              <h1>Text Stream</h1>
              <p>Real-time SMS messaging interface</p>
            </div>
          </div>
          
          <div className="dashboard-header__center">
            <div className="header-stats">
              <span className="message-count-header">
                {totalCount} message{totalCount !== 1 ? 's' : ''}
              </span>
              <div className="connection-status-header">
                <span 
                  className={`connection-indicator ${isConnected ? 'connected' : 'disconnected'}`}
                  title={isConnected ? 'Real-time connected' : 'Real-time disabled (ngrok detected)'}
                >
                  {isConnected ? '🟢 Connected' : '🔴 Real-time disabled'}
                </span>
              </div>
            </div>
          </div>

          <div className="dashboard-header__right">
            <button 
              onClick={handleRefresh}
              className="header-refresh-button"
              disabled={loading}
              type="button"
              title="Refresh messages"
            >
              {loading ? '⟳' : '↻'}
            </button>
          </div>
        </div>
      </header>
      
      <main className="dashboard-content">
        <div className="dashboard-grid">
          <div className="message-panel">
            <MessageHistory 
              onRefresh={() => {
                console.log('Messages refreshed manually');
              }}
            />
          </div>
          
          <div className="send-panel">
            <SendForm />
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;